import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import RenderOnQueries from '../Layout/RenderOnQueries'
import ResourceScheduleStore from './ResourceScheduleStore'
import SessionStore from '../../State/SessionStore'
import PhaseCollection from '../../State/Collections/PhaseCollection'
import ProjectCollection from '../../State/Collections/ProjectCollection'
import { queryClient } from '../../App'
import { format, set } from 'date-fns'
import {
    canEditRevenueTargets,
    canEditStaffAllocations,
    canViewProjectFees,
    canViewRevenueTargets,
    canViewStaffAllocations,
    canViewStaffCostRate,
    shouldUpdateHoursFromRevenue,
    shouldUpdateRevenueFromHours,
} from '../../State/Permissions/HasPermissions'
import { Checkbox } from 'antd'
import RevenueProgressInput from '../../Components/ForecastSidebar/RevenueProgressInput'
import _ from 'lodash'
import ExpenseProgressInput from '../../Components/ForecastSidebar/ExpenseProgressInput'
import UtilisationProgressInput from '../../Components/ForecastSidebar/UtilisationProgressInput'
import StaffCollection from '../../State/Collections/StaffCollection'
import getCombinedRateInDateRange from '../../Utils/getCombinedRateInDateRange'
import RoleCollection from '../../State/Collections/RoleCollection'
import SidebarTitle from '../../Components/ForecastSidebar/SidebarTitle'
import SidebarNavigation from '../../Components/ForecastSidebar/SidebarNavigation'
import { action, computed, makeObservable, observable, toJS } from 'mobx'
import { makeRequest } from '../../Queries/makeRequest'
import { FormatCurrency } from '../../Utils/Localisation/CurrencyFormatter'
import { Link } from '@tanstack/react-router'
import sortPhases from '../../Utils/sortPhases'
import { StaffSelector } from '../../Components/Selector'
import HeaderButton from '../../Components/ui/HeaderButton'
import { trpc } from '../../system/trpc'
import { processTRPCData } from '../../Queries/fetchData'

export default observer(() => {
    const [store, setStore] = useState(ResourceScheduleStore)
    const period =
        store.selectedPeriod && store.startOfPeriod(store.selectedPeriod)
    const project = ProjectCollection.projectsById[store.selectedProjectId]
    const phase = PhaseCollection.phasesById[store.selectedPhaseId]
    const periodId = format(period, store.periodFormat())
    const periodType = store.periodType
    const queryId = `sidebar${periodType}${periodId}${store.selectedProjectId}${
        store.selectedPhaseId
    }${store.selectedStaffId}${
        store.selectedRoleId
    }${store.selectedStaffIds.join(',')}`
    if (store.selectedPeriod && store.selectedObject) {
        return (
            <RenderOnQueries
                queryIds={[
                    {
                        id: queryId,
                        baseURL: process.env.REACT_APP_NODE_SERVER_URL,
                        path: `/project-forecast-sidebar`,
                        method: 'POST',
                        staleTime: 0,
                        data: {
                            projectId: store.selectedProjectId,
                            organisationId: SessionStore.organisationId,
                            userId: SessionStore.user?.id,
                            invoiceType:
                                SessionStore.organisation
                                    .defaultRevenueForecastReport.filters
                                    .invoiceData,
                            periodType: ResourceScheduleStore.periodType,
                            dataType:
                                ResourceScheduleStore.report.filters.hoursData,
                            selectedPeriod: format(
                                new Date(store.selectedPeriod),
                                store.periodFormat()
                            ),
                            phaseId: store.selectedPhaseId,
                            staffIds: store.selectedStaffIds,
                        },
                    },
                    {
                        id: store.selectedProjectId + 'phases',
                        queryFn: async () => {
                            const requestData =
                                await trpc.legacy.getProjectPhases.query({
                                    projectId: store.selectedProjectId,
                                })
                            return processTRPCData(
                                requestData,
                                `phases-${store.selectedProjectId}`
                            )
                        },
                    },
                    {
                        id: `rates-${store.selectedProjectId}`,
                        queryFn: async () => {
                            const requestData =
                                await trpc.legacy.getRates.query({
                                    projectId: store.selectedProjectId,
                                })
                            return processTRPCData(
                                requestData,
                                `rates-${store.selectedProjectId}`
                            )
                        },
                    },
                ]}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }}
                    key={`sidebar${periodType}${periodId}${
                        store.selectedProjectId
                    }${store.selectedPhaseId}${store.selectedStaffId}${
                        store.selectedRoleId
                    }${store.selectedStaffIds.join(',')}`}
                >
                    <div>
                        <SidebarTitle
                            title={project?.title}
                            subTitle={phase?.title}
                            link={`/projects/${store.selectedProjectId}`}
                        />
                        <SidebarNavigation
                            title={format(
                                store.selectedPeriod,
                                store.periodDisplayFormat()
                            )}
                            onLeftClick={() => store.shiftSelectedPeriod(-1)}
                            onRightClick={() => store.shiftSelectedPeriod(1)}
                        />
                        <ProjectSidebarContent
                            periodType={periodType}
                            period={period}
                            periodId={periodId}
                            projectId={store.selectedProjectId}
                            phaseId={store.selectedPhaseId}
                            staffId={store.selectedStaffId}
                            roleId={store.selectedRoleId}
                            staffIds={store.selectedStaffIds}
                            queryId={queryId}
                            onUpdateHours={(hoursData) => {
                                const groups =
                                    ResourceScheduleStore.report.filters.groups
                                const hoursByRow = new Map()
                                hoursData.forEach((hd) => {
                                    let row
                                    const {
                                        period: periodFromData,
                                        hours,
                                        ...models
                                    } = hd
                                    models.owner = models.project?.owner
                                    groups.forEach((group, i) => {
                                        const children = row
                                            ? row.children
                                            : ResourceScheduleStore.queryData
                                        const groupsBefore = groups.slice(
                                            0,
                                            i + 1
                                        )
                                        const key = groupsBefore
                                            ?.map(
                                                (g) =>
                                                    models[g]?.id ||
                                                    models[g] ||
                                                    'null'
                                            )
                                            .join('||')
                                        row = children.find(
                                            (r) => r.key === key
                                        )
                                    })
                                    if (row) {
                                        if (hoursByRow.has(row)) {
                                            const oldHours = hoursByRow.get(row)
                                            hoursByRow.set(
                                                row,
                                                hours + oldHours
                                            )
                                        } else {
                                            hoursByRow.set(row, hours)
                                        }
                                    }
                                })
                                hoursByRow.forEach((hours, row) => {
                                    ResourceScheduleStore.setHours(
                                        row,
                                        periodId,
                                        hours,
                                        false
                                    )
                                })
                            }}
                        />
                    </div>
                </div>
            </RenderOnQueries>
        )
    }
})

class SidebarStore {
    @observable sidebarId = null
    @observable period = null
    @observable periodType = 'month'
    @observable projectId = null
    @observable queryData = {
        revenueData: [],
        hoursData: [],
        phases: [],
        staff: [],
        roles: [],
    }
    @observable updateRevenueWithExpenses = null
    @observable updateExpensesWithRevenue = null
    @observable showAllPhases = false
    constructor() {
        makeObservable(this)
    }
    @action.bound
    setQueryData(data, projectId, periodType, period, sidebarId) {
        this.queryData = data
        this.periodType = periodType
        this.period = period
        this.projectId = projectId
        this.sidebarId = sidebarId
        this.updateRevenueWithExpenses ??= shouldUpdateRevenueFromHours()
        this.updateExpensesWithRevenue ??= shouldUpdateHoursFromRevenue()
    }
    @action.bound
    setUpdateExpensesWithRevenue(value) {
        this.updateExpensesWithRevenue = value
    }
    @action.bound
    setUpdateRevenueWithExpenses(value) {
        this.updateRevenueWithExpenses = value
    }
    @action.bound
    setShowAllPhases(value) {
        this.showAllPhases = value
    }
    @action.bound
    setHoursInPeriod(
        period,
        projectId,
        phaseId,
        roleId,
        staffId,
        hours,
        phaseIds,
        staffIds,
        periodType
    ) {
        this.queryData ??= {}
        const correctSidebar =
            period === this.period &&
            projectId === this.projectId &&
            this.queryData != null
        const sidebarId = [period, projectId, phaseId, roleId, staffId].join(
            ', '
        )
        let filteredHoursData = correctSidebar
            ? filterHoursData(
                  this.queryData?.hoursData || [],
                  staffId,
                  roleId,
                  projectId,
                  phaseId
              )
            : []
        if (!filteredHoursData.length) {
            filteredHoursData.push({
                projectId,
                phaseId,
                roleId,
                staffId,
                hoursInPeriod: 0,
                costInPeriod: 0,
                payInPeriod: 0,
                chargeOutInPeriod: 0,
            })
        }
        setHoursInPeriod(filteredHoursData, hours, this.showAllPhases)
        if (this.updateRevenueWithExpenses && correctSidebar) {
            setRevenueProgressFromCost(
                this.queryData.revenueData,
                this.queryData.hoursData,
                this.showAllPhases
            )
            // queryData.revenueData.forEach((d) => {
            //     const phase = PhaseCollection.phasesById[d.phaseId]
            //     onUpdateRevenue({
            //         period: periodId,
            //         revenue: d.revenueInPeriod,
            //         project,
            //         phase,
            //     })
            // })
        }
        this.queryData = {
            ...this.queryData,
        }
        saveChanges(sidebarId, {
            period: period,
            periodType: periodType || this.periodType,
            organisationId: SessionStore.organisationId,
            hoursData: filteredHoursData,
            revenueData: correctSidebar ? this.queryData.revenueData : [],
        })
    }
    @action.bound
    setRevenueInPeriod(period, projectId, phaseId, revenue) {
        this.queryData ??= {}
        const correctSidebar =
            period === this.period &&
            projectId === this.projectId &&
            this.queryData != null

        const sidebarId = [period, projectId, phaseId].join(', ')
        let filteredRevenueData = correctSidebar
            ? this.queryData?.revenueData?.filter(
                  (d) => !phaseId || d.phaseId === phaseId
              ) || []
            : []
        if (!filteredRevenueData.length) {
            filteredRevenueData.push({
                projectId,
                phaseId,
                revenueInPeriod: 0,
            })
        }
        setRevenueInPeriod(filteredRevenueData, revenue, this.showAllPhases)
        if (this.updateExpensesWithRevenue && correctSidebar) {
            setCostProgressFromRevenue(
                this.queryData.revenueData,
                this.queryData.hoursData,
                this.showAllPhases
            )
            // queryData.hoursData.forEach((d) => {
            //     const staff = StaffCollection.staffById[d.staffId]
            //     const role = RoleCollection.rolesById[d.roleId] || staff?.role
            //     const phase = PhaseCollection.phasesById[d.phaseId]
            //     onUpdateHours({
            //         period: periodId,
            //         hours: d.hoursInPeriod,
            //         project,
            //         phase,
            //         staff,
            //         role,
            //     })
            // })
        }
        this.queryData = {
            ...this.queryData,
        }
        saveChanges(sidebarId, {
            period: period,
            periodType: this.periodType,
            organisationId: SessionStore.organisationId,
            revenueData: filteredRevenueData,
            hoursData: correctSidebar ? this.queryData.hoursData : [],
        })
    }
}

export const ProjectSidebarStore = new SidebarStore()

export const ProjectSidebarContent = observer(
    ({
        period,
        periodId,
        periodType,
        projectId,
        phaseId,
        staffId,
        roleId,
        staffIds = [],
        queryId,
        onUpdateHours = () => null,
        onUpdateRevenue = () => null,
    }) => {
        const sidebarId = [
            periodType,
            periodId,
            projectId,
            phaseId,
            staffId,
            roleId,
            staffIds.join(','),
        ].join(', ')
        const project = ProjectCollection.projectsById[projectId]
        const phase = PhaseCollection.phasesById[phaseId]
        useEffect(() => {
            if (phase && phase?.status !== 'active') {
                ProjectSidebarStore.setShowAllPhases(true)
            }
        }, [phase])
        const periodStart = ResourceScheduleStore.startOfPeriod(period)
        const periodEnd = ResourceScheduleStore.endOfPeriod(period)
        const queryData = ProjectSidebarStore.queryData
        const setQueryData = ProjectSidebarStore.setQueryData
        const updateExpensesWithRevenue =
            ProjectSidebarStore.updateExpensesWithRevenue
        const updateRevenueWithExpenses =
            ProjectSidebarStore.updateRevenueWithExpenses
        const setUpdateExpensesWithRevenue =
            ProjectSidebarStore.setUpdateExpensesWithRevenue
        const setUpdateRevenueWithExpenses =
            ProjectSidebarStore.setUpdateRevenueWithExpenses
        const queriesData = queryClient.getQueryData([queryId])?.data
        const showAllPhases = ProjectSidebarStore.showAllPhases
        useEffect(() => {
            setQueryData(
                queriesData,
                projectId,
                periodType,
                periodId,
                sidebarId
            )
            return () => {
                setQueryData({
                    revenueData: [],
                    hoursData: [],
                    phases: [],
                    staff: [],
                    roles: [],
                })
                queryClient.removeQueries([queryId])
            }
        }, [
            projectId,
            phaseId,
            staffId,
            roleId,
            periodType,
            periodId,
            queriesData,
        ])

        // Filtered data for contexts where staff selection is primary (Staff Budgets, Utilisation, overall expenses)
        const staffContextFilteredHoursData = filterHoursData(
            queryData?.hoursData || [],
            staffId, // staffId prop from global store selection
            roleId, // roleId prop from global store selection
            projectId,
            phaseId
        )

        // Filtered data specifically for RoleExpenseBudgets
        // This should not be filtered by staffId, but can be by roleId
        const roleContextFilteredHoursData = filterHoursData(
            queryData?.hoursData || [],
            null, // Explicitly do not filter by the globally selected staffId
            roleId, // Filter by globally selected roleId if present
            projectId,
            phaseId
        )

        const showRevenue =
            periodType === 'month' &&
            canViewProjectFees(SessionStore.user, project) &&
            canViewRevenueTargets(SessionStore.user, project)

        const showExpenses =
            canViewStaffAllocations(SessionStore.user, project) &&
            canViewStaffCostRate(SessionStore.user)

        if (!queryData) {
            return null
        }

        return (
            <div key={sidebarId} style={{ padding: '1em' }}>
                <div className="text-right pb-4">
                    <Checkbox
                        onChange={(e) => {
                            ProjectSidebarStore.setShowAllPhases(
                                e.target.checked
                            )
                        }}
                        checked={ProjectSidebarStore.showAllPhases}
                    >
                        Show All Phases
                    </Checkbox>
                </div>
                {showRevenue ? (
                    <>
                        <h4>
                            Revenue
                            {!!project &&
                            canEditStaffAllocations(
                                SessionStore.user,
                                project
                            ) ? (
                                <Checkbox
                                    onChange={(e) => {
                                        const { checked } = e.target
                                        setUpdateExpensesWithRevenue(checked)
                                    }}
                                    checked={updateExpensesWithRevenue}
                                    style={{
                                        width: '45%',
                                        float: 'right',
                                        fontSize: '0.65em',
                                    }}
                                >
                                    Update Expenses With Revenue
                                </Checkbox>
                            ) : null}
                        </h4>
                        <div style={{ padding: '1em' }}>
                            {!phaseId ? (
                                <RevenueProgressInput
                                    key={'rev' + periodId + projectId}
                                    label={project?.title}
                                    editable={canEditRevenueTargets(
                                        SessionStore.user
                                    )}
                                    month={period}
                                    fee={getTotalFee(queryData.revenueData)}
                                    previousRevenue={getRevenueBeforePeriod(
                                        queryData.revenueData
                                    )}
                                    currentRevenue={getRevenueInPeriod(
                                        queryData.revenueData
                                    )}
                                    onChange={(v) => {
                                        setRevenueInPeriod(
                                            queryData.revenueData,
                                            v,
                                            showAllPhases
                                        )
                                        queryData.revenueData.forEach((d) => {
                                            const phase =
                                                PhaseCollection.phasesById[
                                                    d.phaseId
                                                ]
                                            onUpdateRevenue({
                                                period: periodId,
                                                revenue: d.revenueInPeriod,
                                                project,
                                                phase,
                                            })
                                        })
                                        if (updateExpensesWithRevenue) {
                                            setCostProgressFromRevenue(
                                                queryData.revenueData,
                                                queryData.hoursData,
                                                showAllPhases
                                            )
                                            onUpdateHours(
                                                queryData?.hoursData?.map(
                                                    (hd) => {
                                                        const staff =
                                                            StaffCollection
                                                                .staffById[
                                                                hd.staffId
                                                            ]
                                                        const phase =
                                                            PhaseCollection
                                                                .phasesById[
                                                                hd.phaseId
                                                            ]
                                                        const role =
                                                            RoleCollection
                                                                .rolesById[
                                                                hd.roleId
                                                            ] || staff?.role
                                                        return {
                                                            period: periodId,
                                                            hours: hd.hoursInPeriod,
                                                            project,
                                                            phase,
                                                            staff,
                                                            role,
                                                        }
                                                    }
                                                ) || []
                                            )
                                        }
                                        setQueryData({
                                            ...queryData,
                                        })
                                        saveChanges(sidebarId, {
                                            period: periodId,
                                            periodType,
                                            organisationId:
                                                SessionStore.organisationId,
                                            hoursData: queryData.hoursData,
                                            revenueData: queryData.revenueData,
                                        })
                                    }}
                                />
                            ) : null}
                            <div style={{ padding: '2em 0' }}>
                                {(
                                    queryData?.phases?.filter(
                                        (ph) =>
                                            showAllPhases ||
                                            ph.status === 'active'
                                    ) || []
                                )
                                    .sort(sortPhases)
                                    .map((ph) => {
                                        const phase =
                                            PhaseCollection.phasesById[ph?.id]
                                        const revData =
                                            queryData?.revenueData?.filter(
                                                (d) => d.phaseId === ph?.id
                                            )
                                        return (
                                            <RevenueProgressInput
                                                key={'rev' + periodId + ph?.id}
                                                label={phase?.title}
                                                editable={
                                                    canEditRevenueTargets(
                                                        SessionStore.user
                                                    ) &&
                                                    (showAllPhases
                                                        ? phase.status !==
                                                          'archived'
                                                        : phase.status ===
                                                          'active')
                                                }
                                                month={period}
                                                fee={_.sum(
                                                    revData?.map((d) => d.fee)
                                                )}
                                                previousRevenue={_.sum(
                                                    revData?.map(
                                                        (d) =>
                                                            d.revenueBeforePeriod
                                                    )
                                                )}
                                                currentRevenue={_.sum(
                                                    revData?.map(
                                                        (d) => d.revenueInPeriod
                                                    )
                                                )}
                                                onChange={(v) => {
                                                    setRevenueInPeriod(
                                                        revData,
                                                        v,
                                                        showAllPhases
                                                    )
                                                    revData?.forEach((d) => {
                                                        const phase =
                                                            PhaseCollection
                                                                .phasesById[
                                                                d.phaseId
                                                            ]
                                                        onUpdateRevenue({
                                                            period: periodId,
                                                            revenue:
                                                                d.revenueInPeriod,
                                                            project,
                                                            phase,
                                                        })
                                                    })
                                                    if (
                                                        updateExpensesWithRevenue
                                                    ) {
                                                        setCostProgressFromRevenue(
                                                            queryData.revenueData,
                                                            queryData.hoursData,
                                                            showAllPhases
                                                        )
                                                        onUpdateHours(
                                                            queryData?.hoursData?.map(
                                                                (hd) => {
                                                                    const staff =
                                                                        StaffCollection
                                                                            .staffById[
                                                                            hd
                                                                                .staffId
                                                                        ]
                                                                    const phase =
                                                                        PhaseCollection
                                                                            .phasesById[
                                                                            hd
                                                                                .phaseId
                                                                        ]
                                                                    const role =
                                                                        RoleCollection
                                                                            .rolesById[
                                                                            hd
                                                                                .roleId
                                                                        ] ||
                                                                        staff?.role
                                                                    return {
                                                                        period: periodId,
                                                                        hours: hd.hoursInPeriod,
                                                                        project,
                                                                        phase,
                                                                        staff,
                                                                        role,
                                                                    }
                                                                }
                                                            ) || []
                                                        )
                                                    }
                                                    setQueryData({
                                                        ...queryData,
                                                    })
                                                    saveChanges(sidebarId, {
                                                        period: periodId,
                                                        periodType,
                                                        organisationId:
                                                            SessionStore.organisationId,
                                                        hoursData:
                                                            queryData.hoursData,
                                                        revenueData:
                                                            queryData.revenueData,
                                                    })
                                                }}
                                            />
                                        )
                                    })}
                            </div>
                        </div>
                    </>
                ) : null}
                {showExpenses ? (
                    <>
                        {' '}
                        <h4>
                            Expenses
                            {canEditRevenueTargets(
                                SessionStore.user,
                                project
                            ) && showRevenue ? (
                                <Checkbox
                                    onChange={(e) => {
                                        const { checked } = e.target
                                        setUpdateRevenueWithExpenses(checked)
                                    }}
                                    checked={updateRevenueWithExpenses}
                                    style={{
                                        width: '45%',
                                        float: 'right',
                                        fontSize: '0.65em',
                                    }}
                                >
                                    Update Revenue With Expenses
                                </Checkbox>
                            ) : null}
                        </h4>
                        <div style={{ padding: '1em' }}>
                            {!phaseId ? (
                                <ExpenseProgressInput
                                    editable={
                                        !!project &&
                                        canEditStaffAllocations(
                                            SessionStore.user,
                                            project
                                        )
                                    }
                                    month={period}
                                    onChange={(v) => {
                                        setCostInPeriod(
                                            staffContextFilteredHoursData,
                                            v,
                                            showAllPhases
                                        )
                                        if (
                                            updateRevenueWithExpenses &&
                                            showRevenue
                                        ) {
                                            setRevenueProgressFromCost(
                                                queryData.revenueData,
                                                queryData.hoursData,
                                                showAllPhases
                                            )
                                            queryData.revenueData.forEach(
                                                (d) => {
                                                    const phase =
                                                        PhaseCollection
                                                            .phasesById[
                                                            d.phaseId
                                                        ]
                                                    onUpdateRevenue({
                                                        period: periodId,
                                                        revenue:
                                                            d.revenueInPeriod,
                                                        project,
                                                        phase,
                                                    })
                                                }
                                            )
                                        }
                                        setQueryData({
                                            ...queryData,
                                        })
                                        onUpdateHours(
                                            staffContextFilteredHoursData?.map(
                                                (hd) => {
                                                    const staff =
                                                        StaffCollection
                                                            .staffById[
                                                            hd.staffId
                                                        ]
                                                    const phase =
                                                        PhaseCollection
                                                            .phasesById[
                                                            hd.phaseId
                                                        ]
                                                    const role =
                                                        RoleCollection
                                                            .rolesById[
                                                            hd.roleId
                                                        ] || staff?.role
                                                    return {
                                                        period: periodId,
                                                        hours: hd.hoursInPeriod,
                                                        project,
                                                        phase,
                                                        staff,
                                                        role,
                                                    }
                                                }
                                            ) || []
                                        )
                                        saveChanges(sidebarId, {
                                            period: periodId,
                                            periodType,
                                            organisationId:
                                                SessionStore.organisationId,
                                            hoursData:
                                                staffContextFilteredHoursData,
                                            revenueData: queryData.revenueData,
                                        })
                                    }}
                                    onTimeChange={(v) => {
                                        setHoursInPeriod(
                                            staffContextFilteredHoursData,
                                            v,
                                            showAllPhases
                                        )
                                        if (
                                            updateRevenueWithExpenses &&
                                            showRevenue
                                        ) {
                                            setRevenueProgressFromCost(
                                                queryData.revenueData,
                                                queryData.hoursData,
                                                showAllPhases
                                            )
                                            queryData.revenueData.forEach(
                                                (d) => {
                                                    const phase =
                                                        PhaseCollection
                                                            .phasesById[
                                                            d.phaseId
                                                        ]
                                                    onUpdateRevenue({
                                                        period: periodId,
                                                        revenue:
                                                            d.revenueInPeriod,
                                                        project,
                                                        phase,
                                                    })
                                                }
                                            )
                                        }
                                        setQueryData({
                                            ...queryData,
                                        })
                                        onUpdateHours(
                                            staffContextFilteredHoursData?.map(
                                                (hd) => {
                                                    const staff =
                                                        StaffCollection
                                                            .staffById[
                                                            hd.staffId
                                                        ]
                                                    const phase =
                                                        PhaseCollection
                                                            .phasesById[
                                                            hd.phaseId
                                                        ]
                                                    const role =
                                                        RoleCollection
                                                            .rolesById[
                                                            hd.roleId
                                                        ] || staff?.role
                                                    return {
                                                        period: periodId,
                                                        hours: hd.hoursInPeriod,
                                                        project,
                                                        phase,
                                                        staff,
                                                        role,
                                                    }
                                                }
                                            ) || []
                                        )
                                        saveChanges(sidebarId, {
                                            period: periodId,
                                            periodType,
                                            organisationId:
                                                SessionStore.organisationId,
                                            hoursData:
                                                staffContextFilteredHoursData,
                                            revenueData: queryData.revenueData,
                                        })
                                    }}
                                    costBudget={getCostBudget(
                                        staffContextFilteredHoursData
                                    )}
                                    hoursBudget={getHoursBudget(
                                        staffContextFilteredHoursData
                                    )}
                                    previousCost={getCostBeforePeriod(
                                        staffContextFilteredHoursData
                                    )}
                                    previousHours={getHoursBeforePeriod(
                                        staffContextFilteredHoursData
                                    )}
                                    currentCost={getCostInPeriod(
                                        staffContextFilteredHoursData
                                    )}
                                    currentHours={getHoursInPeriod(
                                        staffContextFilteredHoursData
                                    )}
                                    key={'res' + periodId + projectId}
                                    label={project?.title}
                                />
                            ) : null}
                            <div style={{ padding: '2em 0' }}>
                                {queryData?.phases
                                    ?.filter(
                                        (ph) =>
                                            showAllPhases ||
                                            ph.status === 'active'
                                    )
                                    .sort(sortPhases)
                                    .map((ph) => {
                                        const phase =
                                            PhaseCollection.phasesById[ph?.id]
                                        const hoursData =
                                            staffContextFilteredHoursData?.filter(
                                                (d) => d.phaseId === ph?.id
                                            ) || []
                                        return (
                                            <ExpenseProgressInput
                                                editable={
                                                    !!project &&
                                                    canEditStaffAllocations(
                                                        SessionStore.user,
                                                        project
                                                    ) &&
                                                    (showAllPhases
                                                        ? phase.status !==
                                                          'archived'
                                                        : phase.status ===
                                                          'active')
                                                }
                                                month={period}
                                                onChange={(v) => {
                                                    setCostInPeriod(
                                                        hoursData,
                                                        v,
                                                        showAllPhases
                                                    )
                                                    if (
                                                        updateRevenueWithExpenses &&
                                                        showRevenue
                                                    ) {
                                                        setRevenueProgressFromCost(
                                                            queryData.revenueData,
                                                            queryData.hoursData,
                                                            showAllPhases
                                                        )
                                                        queryData.revenueData.forEach(
                                                            (d) => {
                                                                const phase =
                                                                    PhaseCollection
                                                                        .phasesById[
                                                                        d
                                                                            .phaseId
                                                                    ]
                                                                onUpdateRevenue(
                                                                    {
                                                                        period: periodId,
                                                                        revenue:
                                                                            d.revenueInPeriod,
                                                                        project,
                                                                        phase,
                                                                    }
                                                                )
                                                            }
                                                        )
                                                    }
                                                    setQueryData({
                                                        ...queryData,
                                                    })
                                                    onUpdateHours(
                                                        hoursData?.map((hd) => {
                                                            const staff =
                                                                StaffCollection
                                                                    .staffById[
                                                                    hd.staffId
                                                                ]
                                                            const role =
                                                                RoleCollection
                                                                    .rolesById[
                                                                    hd.roleId
                                                                ] || staff?.role
                                                            return {
                                                                period: periodId,
                                                                hours: hd.hoursInPeriod,
                                                                project,
                                                                phase,
                                                                staff,
                                                                role,
                                                            }
                                                        }) || []
                                                    )
                                                    saveChanges(sidebarId, {
                                                        period: periodId,
                                                        periodType,
                                                        organisationId:
                                                            SessionStore.organisationId,
                                                        hoursData: hoursData,
                                                        revenueData:
                                                            queryData.revenueData,
                                                    })
                                                }}
                                                onTimeChange={(v) => {
                                                    setHoursInPeriod(
                                                        hoursData,
                                                        v,
                                                        showAllPhases
                                                    )
                                                    if (
                                                        updateRevenueWithExpenses &&
                                                        showRevenue
                                                    ) {
                                                        setRevenueProgressFromCost(
                                                            queryData.revenueData,
                                                            queryData.hoursData,
                                                            showAllPhases
                                                        )
                                                        queryData.revenueData.forEach(
                                                            (d) => {
                                                                const phase =
                                                                    PhaseCollection
                                                                        .phasesById[
                                                                        d
                                                                            .phaseId
                                                                    ]
                                                                onUpdateRevenue(
                                                                    {
                                                                        period: periodId,
                                                                        revenue:
                                                                            d.revenueInPeriod,
                                                                        project,
                                                                        phase,
                                                                    }
                                                                )
                                                            }
                                                        )
                                                    }
                                                    setQueryData({
                                                        ...queryData,
                                                    })
                                                    onUpdateHours(
                                                        hoursData?.map((hd) => {
                                                            const staff =
                                                                StaffCollection
                                                                    .staffById[
                                                                    hd.staffId
                                                                ]
                                                            const role =
                                                                RoleCollection
                                                                    .rolesById[
                                                                    hd.roleId
                                                                ] || staff?.role
                                                            return {
                                                                period: periodId,
                                                                hours: hd.hoursInPeriod,
                                                                project,
                                                                phase,
                                                                staff,
                                                                role,
                                                            }
                                                        }) || []
                                                    )
                                                    saveChanges(sidebarId, {
                                                        period: periodId,
                                                        periodType,
                                                        organisationId:
                                                            SessionStore.organisationId,
                                                        hoursData: hoursData,
                                                        revenueData:
                                                            queryData.revenueData,
                                                    })
                                                }}
                                                costBudget={getCostBudget(
                                                    hoursData
                                                )}
                                                hoursBudget={getHoursBudget(
                                                    hoursData
                                                )}
                                                previousCost={getCostBeforePeriod(
                                                    hoursData
                                                )}
                                                previousHours={getHoursBeforePeriod(
                                                    hoursData
                                                )}
                                                currentCost={getCostInPeriod(
                                                    hoursData
                                                )}
                                                currentHours={getHoursInPeriod(
                                                    hoursData
                                                )}
                                                key={'res' + periodId + ph?.id}
                                                label={phase?.title}
                                            />
                                        )
                                    })}
                            </div>
                        </div>
                    </>
                ) : null}
                {showExpenses ? (
                    <RoleExpenseBudgets
                        project={project}
                        period={period}
                        periodId={periodId}
                        periodType={periodType}
                        filteredHoursData={roleContextFilteredHoursData}
                        queryData={queryData}
                        sidebarId={sidebarId}
                        updateRevenueWithExpenses={updateRevenueWithExpenses}
                        showRevenue={showRevenue}
                        setQueryData={setQueryData}
                        onUpdateHours={onUpdateHours}
                        onUpdateRevenue={onUpdateRevenue}
                        showAllPhases={showAllPhases}
                        editable={
                            !!project &&
                            canEditStaffAllocations(SessionStore.user, project)
                        }
                    />
                ) : null}
                {showExpenses ? (
                    <StaffExpenseBudgets
                        project={project}
                        period={period}
                        periodId={periodId}
                        periodType={periodType}
                        filteredHoursData={staffContextFilteredHoursData}
                        queryData={queryData}
                        sidebarId={sidebarId}
                        updateRevenueWithExpenses={updateRevenueWithExpenses}
                        showRevenue={showRevenue}
                        setQueryData={setQueryData}
                        onUpdateHours={onUpdateHours}
                        onUpdateRevenue={onUpdateRevenue}
                        showAllPhases={showAllPhases}
                        editable={
                            !!project &&
                            canEditStaffAllocations(SessionStore.user, project)
                        }
                    />
                ) : null}
                <RenderOnQueries
                    queryIds={[
                        {
                            id: `staff-util-${project?.id}-${periodId}`,
                            baseURL: process.env.REACT_APP_NODE_SERVER_URL,
                            path: `/staff-utilisation/without-project`,
                            method: 'POST',
                            data: {
                                period: periodId,
                                periodType: periodType,
                                projectId: project?.id,
                                organisationId: SessionStore.organisationId,
                                userId: SessionStore.user?.id,
                            },
                        },
                    ]}
                >
                    <StaffUtilisation
                        staffIds={staffIds}
                        project={project}
                        period={period}
                        periodId={periodId}
                        periodStart={periodStart}
                        periodEnd={periodEnd}
                        periodType={periodType}
                        filteredHoursData={staffContextFilteredHoursData}
                        queryData={queryData}
                        sidebarId={sidebarId}
                        projectId={projectId}
                        phaseId={phaseId}
                        updateRevenueWithExpenses={updateRevenueWithExpenses}
                        showRevenue={showRevenue}
                        setQueryData={setQueryData}
                        onUpdateHours={onUpdateHours}
                        onUpdateRevenue={onUpdateRevenue}
                        showAllPhases={showAllPhases}
                    />
                </RenderOnQueries>
                <RenderOnQueries
                    queryIds={[
                        {
                            id: `rev-source-${projectId}-${periodId}`,
                            baseURL: process.env.REACT_APP_NODE_SERVER_URL,
                            path: `/revenue-sources/`,
                            method: 'POST',
                            data: {
                                period: periodId,
                                periodType: periodType,
                                projectId: projectId,
                                organisationId: SessionStore.organisationId,
                                userId: SessionStore.user?.id,
                                invoiceDateType:
                                    SessionStore.settings.reportInvoiceDateType,
                            },
                        },
                    ]}
                >
                    <RevenueSources
                        project={project}
                        period={period}
                        periodId={periodId}
                    />
                </RenderOnQueries>
            </div>
        )
    }
)

const StaffUtilisation = observer(
    ({
        staffIds,
        project,
        period,
        periodId,
        periodStart,
        periodEnd,
        periodType,
        filteredHoursData,
        queryData,
        sidebarId,
        projectId,
        phaseId,
        updateRevenueWithExpenses,
        showRevenue,
        setQueryData,
        onUpdateHours,
        onUpdateRevenue,
        showAllPhases,
    }) => {
        const staffUtilData = queryClient.getQueryData([
            `staff-util-${project?.id}-${periodId}`,
        ])?.data
        const uniqueStaffIds = [
            ...new Set(
                [
                    ...staffIds,
                    ...filteredHoursData?.map((d) => d.staffId),
                ]?.filter((stId) => {
                    const staff = StaffCollection.staffById[stId]
                    const hoursData =
                        filteredHoursData?.filter(
                            (d) => d.staffId === staff?.id
                        ) || []
                    const totalHours = getHoursInPeriod(hoursData)
                    return totalHours > 0 || staff?.isArchived === false
                })
            ),
        ]

        const [selectedStaff, setSelectedStaff] = useState(null)

        const handleAddStaff = () => {
            if (!selectedStaff) return

            // Get phaseId - either use provided phaseId or find first active phase
            let targetPhaseId = phaseId
            if (!targetPhaseId && queryData?.phases?.length) {
                const activePhases = queryData?.phases
                    ?.filter((ph) => ph.status === 'active')
                    .sort(sortPhases)
                if (activePhases.length) {
                    targetPhaseId = activePhases[0].id
                } else {
                    // If no active phases, use first non-archived phase
                    const nonArchivedPhases = queryData?.phases
                        ?.filter((ph) => ph.status !== 'archived')
                        .sort(sortPhases)
                    if (nonArchivedPhases.length) {
                        targetPhaseId = nonArchivedPhases[0].id
                    }
                }
            }

            const targetPhase = PhaseCollection.phasesById[targetPhaseId]

            // Create initial hoursData entry for the new staff
            const newHoursData = {
                staffId: selectedStaff.id,
                roleId: selectedStaff.roleId,
                projectId: project.id,
                phaseId: targetPhaseId,
                hoursInPeriod: 0,
                [periodType]: periodId,
                costInPeriod: 0,
                payInPeriod: 0,
                chargeOutInPeriod: 0,
            }

            // Update filteredHoursData and queryData
            setQueryData({
                ...queryData,
                hoursData: [...queryData.hoursData, newHoursData],
            })

            // Add staff using ResourceScheduleStore
            ResourceScheduleStore.addStaffs(
                [selectedStaff],
                project,
                targetPhase,
                [periodStart, periodEnd],
                0, // Initial hours
                'hours',
                (period, newHours, projectId, phaseId, roleId, staffId) => {
                    ProjectSidebarStore.setHoursInPeriod(
                        period,
                        projectId,
                        phaseId || null,
                        roleId || null,
                        staffId || null,
                        newHours,
                        ResourceScheduleStore.selectedPhaseIds,
                        ResourceScheduleStore.selectedStaffIds,
                        ResourceScheduleStore.periodType
                    )
                }
            )

            // Update the uniqueStaffIds to include the new staff
            if (!uniqueStaffIds.includes(selectedStaff.id)) {
                staffIds.push(selectedStaff.id)
            }

            setSelectedStaff(null)
        }

        return (
            <>
                <h4>Utilisation</h4>
                <div style={{ padding: '1em' }}>
                    {!!project &&
                        canEditStaffAllocations(SessionStore.user, project) && (
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '0.5em',
                                    marginBottom: '1em',
                                }}
                            >
                                <StaffSelector
                                    selectedStaffMember={selectedStaff}
                                    onChange={setSelectedStaff}
                                    staffOptions={StaffCollection.staffs
                                        .filter(
                                            (s) =>
                                                !s.isArchived &&
                                                !uniqueStaffIds.includes(s.id)
                                        )
                                        .sort((a, b) =>
                                            a.fullName.localeCompare(b.fullName)
                                        )}
                                    style={{ flex: 1 }}
                                    variant="secondary"
                                />
                                <HeaderButton
                                    label="+"
                                    onClick={handleAddStaff}
                                    // disabled={!selectedStaff}
                                    style={{ flex: 0 }}
                                    variant="secondary"
                                />
                            </div>
                        )}
                    {uniqueStaffIds
                        .filter(Boolean)
                        .sort((a, b) => {
                            const staffA = StaffCollection.staffById[a]
                            const staffB = StaffCollection.staffById[b]
                            return staffA?.fullName.localeCompare(
                                staffB?.fullName
                            )
                        })
                        .map((stId) => {
                            const staff = StaffCollection.staffById[stId]
                            const hoursData = filteredHoursData.filter(
                                (d) => d.staffId === staff?.id
                            )
                            return (
                                <UtilisationProgressInput
                                    key={
                                        'util' +
                                        staff?.id +
                                        projectId +
                                        periodId
                                    }
                                    label={staff?.fullName}
                                    editable={
                                        !!project &&
                                        canEditStaffAllocations(
                                            SessionStore.user,
                                            project
                                        )
                                    }
                                    month={period}
                                    staff={staff}
                                    availability={staff?.getAvailableHoursInDateRange?.(
                                        [periodStart, periodEnd]
                                    )}
                                    totalHours={
                                        getHoursInPeriod(hoursData) +
                                        (staffUtilData[staff?.id]?.hours ?? 0)
                                    }
                                    selectedHours={getHoursInPeriod(hoursData)}
                                    onChange={(v) => {
                                        setHoursInPeriod(
                                            hoursData,
                                            v,
                                            showAllPhases
                                        )
                                        if (
                                            updateRevenueWithExpenses &&
                                            showRevenue
                                        ) {
                                            setRevenueProgressFromCost(
                                                queryData.revenueData,
                                                queryData.hoursData,
                                                showAllPhases
                                            )
                                            queryData.revenueData.forEach(
                                                (d) => {
                                                    const phase =
                                                        PhaseCollection
                                                            .phasesById[
                                                            d.phaseId
                                                        ]
                                                    onUpdateRevenue({
                                                        period: periodId,
                                                        revenue:
                                                            d.revenueInPeriod,
                                                        project,
                                                        phase,
                                                    })
                                                }
                                            )
                                        }
                                        setQueryData({
                                            ...queryData,
                                        })
                                        onUpdateHours(
                                            hoursData?.map((hd) => {
                                                const staff =
                                                    StaffCollection.staffById[
                                                        hd.staffId
                                                    ]
                                                const phase =
                                                    PhaseCollection.phasesById[
                                                        hd.phaseId
                                                    ]
                                                const role =
                                                    RoleCollection.rolesById[
                                                        hd.roleId
                                                    ] || staff?.role
                                                return {
                                                    period: periodId,
                                                    hours: hd.hoursInPeriod,
                                                    project,
                                                    phase,
                                                    staff,
                                                    role,
                                                }
                                            }) || []
                                        )
                                        saveChanges(sidebarId, {
                                            period: periodId,
                                            periodType,
                                            organisationId:
                                                SessionStore.organisationId,
                                            hoursData: hoursData,
                                            revenueData: queryData.revenueData,
                                        })
                                    }}
                                />
                            )
                        })}
                </div>
            </>
        )
    }
)

const RevenueSources = observer(({ project, period, periodId }) => {
    const sources = queryClient.getQueryData([
        `rev-source-${project?.id}-${periodId}`,
    ])?.data
    if (!sources?.invoices?.length && !sources?.changeLogs?.length) return null
    return (
        <div
            style={{
                borderTop: '1px solid #ccc',
                paddingTop: '1em',
                marginTop: '1em',
            }}
        >
            <h4>Revenue Sources</h4>
            <div style={{ padding: '1em' }}>
                {sources?.invoices?.length ? (
                    <div>
                        <div style={{ fontWeight: 'bold' }}>Invoices</div>
                        {sources?.invoices?.map((source) => {
                            return (
                                <div
                                    key={source?.id}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '0.5em 0',
                                    }}
                                >
                                    <Link to={`/invoices/${source?.id}`}>
                                        {' '}
                                        {source?.ref}
                                    </Link>
                                    <div>{FormatCurrency(source?.amount)}</div>
                                </div>
                            )
                        })}
                    </div>
                ) : null}
                {sources?.changeLogs?.length ? (
                    <div>
                        <div style={{ fontWeight: 'bold' }}>Change Log</div>
                        {sources?.changeLogs?.map((source) => {
                            return (
                                <div
                                    key={source?.id}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '0.5em 0',
                                    }}
                                >
                                    <div>{source?.title}</div>
                                    <div>
                                        {FormatCurrency(
                                            (source.revenue || 0) -
                                                (source.expenses || 0)
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                ) : null}
            </div>
        </div>
    )
})

const StaffExpenseBudgets = observer(
    ({
        project,
        period,
        periodId,
        periodType,
        filteredHoursData,
        queryData,
        sidebarId,
        updateRevenueWithExpenses,
        showRevenue,
        setQueryData,
        onUpdateHours,
        onUpdateRevenue,
        showAllPhases,
        editable,
    }) => {
        // Group hours data by staff ID
        const hoursByStaff = {}

        filteredHoursData.forEach((data) => {
            if (!data.staffId) return

            if (!hoursByStaff[data.staffId]) {
                hoursByStaff[data.staffId] = []
            }

            hoursByStaff[data.staffId].push(data)
        })

        // Filter out staff with no hoursBudget
        const staffWithBudget = Object.keys(hoursByStaff)?.filter((staffId) => {
            const hoursData = hoursByStaff[staffId]
            const budget = getHoursBudget(hoursData)
            return budget > 0
        })

        if (staffWithBudget.length === 0) return null

        return (
            <>
                <h4>Staff Expense Budgets</h4>
                <div style={{ padding: '1em' }}>
                    {staffWithBudget?.map((staffId) => {
                        const staff = StaffCollection.staffById[staffId]
                        const hoursData = hoursByStaff[staffId]

                        return (
                            <ExpenseProgressInput
                                key={`staff-expense-${staffId}-${periodId}`}
                                label={staff?.fullName}
                                editable={
                                    editable &&
                                    (showAllPhases
                                        ? !staff?.isArchived
                                        : !staff?.isArchived)
                                }
                                month={period}
                                onChange={(v) => {
                                    setCostInPeriod(hoursData, v, showAllPhases)
                                    if (
                                        updateRevenueWithExpenses &&
                                        showRevenue
                                    ) {
                                        setRevenueProgressFromCost(
                                            queryData.revenueData,
                                            queryData.hoursData,
                                            showAllPhases
                                        )
                                        queryData.revenueData.forEach((d) => {
                                            const phase =
                                                PhaseCollection.phasesById[
                                                    d.phaseId
                                                ]
                                            onUpdateRevenue({
                                                period: periodId,
                                                revenue: d.revenueInPeriod,
                                                project,
                                                phase,
                                            })
                                        })
                                    }
                                    setQueryData({
                                        ...queryData,
                                    })
                                    onUpdateHours(
                                        hoursData?.map((hd) => {
                                            const staff =
                                                StaffCollection.staffById[
                                                    hd.staffId
                                                ]
                                            const phase =
                                                PhaseCollection.phasesById[
                                                    hd.phaseId
                                                ]
                                            const role =
                                                RoleCollection.rolesById[
                                                    hd.roleId
                                                ] || staff?.role
                                            return {
                                                period: periodId,
                                                hours: hd.hoursInPeriod,
                                                project,
                                                phase,
                                                staff,
                                                role,
                                            }
                                        }) || []
                                    )
                                    saveChanges(sidebarId, {
                                        period: periodId,
                                        periodType,
                                        organisationId:
                                            SessionStore.organisationId,
                                        hoursData: hoursData,
                                        revenueData: queryData.revenueData,
                                    })
                                }}
                                onTimeChange={(v) => {
                                    setHoursInPeriod(
                                        hoursData,
                                        v,
                                        showAllPhases
                                    )
                                    if (
                                        updateRevenueWithExpenses &&
                                        showRevenue
                                    ) {
                                        setRevenueProgressFromCost(
                                            queryData.revenueData,
                                            queryData.hoursData,
                                            showAllPhases
                                        )
                                        queryData.revenueData.forEach((d) => {
                                            const phase =
                                                PhaseCollection.phasesById[
                                                    d.phaseId
                                                ]
                                            onUpdateRevenue({
                                                period: periodId,
                                                revenue: d.revenueInPeriod,
                                                project,
                                                phase,
                                            })
                                        })
                                    }
                                    setQueryData({
                                        ...queryData, //
                                    })
                                    onUpdateHours(
                                        hoursData?.map((hd) => {
                                            const staff =
                                                StaffCollection.staffById[
                                                    hd.staffId
                                                ]
                                            const phase =
                                                PhaseCollection.phasesById[
                                                    hd.phaseId
                                                ]
                                            const role =
                                                RoleCollection.rolesById[
                                                    hd.roleId
                                                ] || staff?.role
                                            return {
                                                period: periodId,
                                                hours: hd.hoursInPeriod,
                                                project,
                                                phase,
                                                staff,
                                                role,
                                            }
                                        }) || []
                                    )
                                    saveChanges(sidebarId, {
                                        period: periodId,
                                        periodType,
                                        organisationId:
                                            SessionStore.organisationId,
                                        hoursData: hoursData,
                                        revenueData: queryData.revenueData,
                                    })
                                }}
                                costBudget={getCostBudget(hoursData)}
                                hoursBudget={getHoursBudget(hoursData)}
                                previousCost={getCostBeforePeriod(hoursData)}
                                previousHours={getHoursBeforePeriod(hoursData)}
                                currentCost={getCostInPeriod(hoursData)}
                                currentHours={getHoursInPeriod(hoursData)}
                            />
                        )
                    })}
                </div>
            </>
        )
    }
)

const RoleExpenseBudgets = observer(
    ({
        project,
        period,
        periodId,
        periodType,
        filteredHoursData,
        queryData,
        sidebarId,
        updateRevenueWithExpenses,
        showRevenue,
        setQueryData,
        onUpdateHours,
        onUpdateRevenue,
        showAllPhases,
        editable,
    }) => {
        // Group hours data by role ID
        const hoursByRole = {}

        filteredHoursData.forEach((data) => {
            if (!data.roleId) return

            if (!hoursByRole[data.roleId]) {
                hoursByRole[data.roleId] = []
            }

            hoursByRole[data.roleId].push(data)
        })

        // Filter out roles with no hoursBudget
        const rolesWithBudget = Object.keys(hoursByRole)?.filter((roleId) => {
            const hoursData = hoursByRole[roleId]
            const budget = getHoursBudget(hoursData)
            return budget > 0
        })

        if (rolesWithBudget.length === 0) return null

        return (
            <>
                <h4>Role Expense Budgets</h4>
                <div style={{ padding: '1em' }}>
                    {rolesWithBudget?.map((roleId) => {
                        const role = RoleCollection.rolesById[roleId]
                        const hoursData = hoursByRole[roleId]

                        return (
                            <ExpenseProgressInput
                                key={`role-expense-${roleId}-${periodId}`}
                                label={role?.name}
                                editable={
                                    editable &&
                                    (showAllPhases
                                        ? !role?.isArchived
                                        : !role?.isArchived)
                                }
                                month={period}
                                onChange={(v) => {
                                    setCostInPeriod(hoursData, v, showAllPhases)
                                    if (
                                        updateRevenueWithExpenses &&
                                        showRevenue
                                    ) {
                                        setRevenueProgressFromCost(
                                            queryData.revenueData,
                                            queryData.hoursData,
                                            showAllPhases
                                        )
                                        queryData.revenueData.forEach((d) => {
                                            const phase =
                                                PhaseCollection.phasesById[
                                                    d.phaseId
                                                ]
                                            onUpdateRevenue({
                                                period: periodId,
                                                revenue: d.revenueInPeriod,
                                                project,
                                                phase,
                                            })
                                        })
                                    }
                                    setQueryData({
                                        ...queryData,
                                    })
                                    onUpdateHours(
                                        hoursData?.map((hd) => {
                                            const staff =
                                                StaffCollection.staffById[
                                                    hd.staffId
                                                ]
                                            const phase =
                                                PhaseCollection.phasesById[
                                                    hd.phaseId
                                                ]
                                            const roleFromHD = // renamed to avoid conflict
                                                RoleCollection.rolesById[
                                                    hd.roleId
                                                ] || staff?.role
                                            return {
                                                period: periodId,
                                                hours: hd.hoursInPeriod,
                                                project,
                                                phase,
                                                staff,
                                                role: roleFromHD,
                                            }
                                        }) || []
                                    )
                                    saveChanges(sidebarId, {
                                        period: periodId,
                                        periodType,
                                        organisationId:
                                            SessionStore.organisationId,
                                        hoursData: hoursData,
                                        revenueData: queryData.revenueData,
                                    })
                                }}
                                onTimeChange={(v) => {
                                    setHoursInPeriod(
                                        hoursData,
                                        v,
                                        showAllPhases
                                    )
                                    if (
                                        updateRevenueWithExpenses &&
                                        showRevenue
                                    ) {
                                        setRevenueProgressFromCost(
                                            queryData.revenueData,
                                            queryData.hoursData,
                                            showAllPhases
                                        )
                                        queryData.revenueData.forEach((d) => {
                                            const phase =
                                                PhaseCollection.phasesById[
                                                    d.phaseId
                                                ]
                                            onUpdateRevenue({
                                                period: periodId,
                                                revenue: d.revenueInPeriod,
                                                project,
                                                phase,
                                            })
                                        })
                                    }
                                    setQueryData({
                                        ...queryData, //
                                    })
                                    onUpdateHours(
                                        hoursData?.map((hd) => {
                                            const staff =
                                                StaffCollection.staffById[
                                                    hd.staffId
                                                ]
                                            const phase =
                                                PhaseCollection.phasesById[
                                                    hd.phaseId
                                                ]
                                            const roleFromHD = // renamed to avoid conflict
                                                RoleCollection.rolesById[
                                                    hd.roleId
                                                ] || staff?.role
                                            return {
                                                period: periodId,
                                                hours: hd.hoursInPeriod,
                                                project,
                                                phase,
                                                staff,
                                                role: roleFromHD,
                                            }
                                        }) || []
                                    )
                                    saveChanges(sidebarId, {
                                        period: periodId,
                                        periodType,
                                        organisationId:
                                            SessionStore.organisationId,
                                        hoursData: hoursData,
                                        revenueData: queryData.revenueData,
                                    })
                                }}
                                costBudget={getCostBudget(hoursData)}
                                hoursBudget={getHoursBudget(hoursData)}
                                previousCost={getCostBeforePeriod(hoursData)}
                                previousHours={getHoursBeforePeriod(hoursData)}
                                currentCost={getCostInPeriod(hoursData)}
                                currentHours={getHoursInPeriod(hoursData)}
                            />
                        )
                    })}
                </div>
            </>
        )
    }
)

const splitValueAmongstObjects = (objects, value, key) => {
    const currentTotal = _.sum(objects.map((o) => o[key]))
    if (currentTotal === 0) {
        objects.forEach((o) => {
            o[key] = value / objects.length
        })
    } else {
        const ratio = value / currentTotal
        objects.forEach((o) => {
            o[key] = o[key] * ratio
        })
    }
    return objects
}

const filterHoursData = (hoursData, staffId, roleId, projectId, phaseId) => {
    return (
        hoursData?.filter((d) => {
            return (
                ((!staffId && !roleId) ||
                    (staffId && staffId === d.staffId) ||
                    (!staffId && roleId === d.roleId)) &&
                (!phaseId || phaseId === d.phaseId) &&
                (!projectId || projectId === d.projectId)
            )
        }) || []
    )
}

const getTotalFee = (revenueData) => {
    return _.sum(revenueData?.map((d) => d.fee || 0) || [])
}
const getRevenueBeforePeriod = (revenueData) => {
    return _.sum(revenueData?.map((d) => d.revenueBeforePeriod || 0) || [])
}
const getRevenueInPeriod = (revenueData) => {
    return _.sum(revenueData?.map((d) => d.revenueInPeriod || 0) || [])
}
const getCostBeforePeriod = (hoursData) => {
    return _.sum(hoursData?.map((d) => d.costBeforePeriod || 0) || [])
}
const getCostInPeriod = (hoursData) => {
    return _.sum(hoursData?.map((d) => d.costInPeriod || 0) || [])
}
const getHoursBeforePeriod = (hoursData) => {
    return _.sum(hoursData?.map((d) => d.hoursBeforePeriod || 0) || [])
}
const getHoursInPeriod = (hoursData) => {
    return _.sum(hoursData?.map((d) => d.hoursInPeriod || 0) || [])
}
const getHoursBudget = (hoursData) => {
    return _.sum(hoursData?.map((d) => d.hoursBudget || 0) || [])
}
const getCostBudget = (hoursData) => {
    return _.sum(
        hoursData?.map((d) => {
            const project = ProjectCollection.projectsById[d.projectId]
            const phase = PhaseCollection.phasesById[d.phaseId]
            const staff = StaffCollection.staffById[d.staffId]
            const role = RoleCollection.rolesById[d?.roleId] || staff?.role
            const models = {
                staff,
                role,
                project,
                phase,
            }
            const rate = getCombinedRateInDateRange(models, 'cost', [
                phase?.startDate,
                phase?.endDate,
            ])
            return d.hoursBudget * rate || 0
        }) || []
    )
}

const setRevenueInPeriod = (revenueData, value, showAllPhases) => {
    const filteredRevenueData =
        revenueData?.filter((d) => {
            const phase = PhaseCollection.phasesById[d.phaseId]
            return (
                phase &&
                (showAllPhases
                    ? phase.status !== 'archived'
                    : phase.status === 'active')
            )
        }) || []
    revenueData = filteredRevenueData.length ? filteredRevenueData : revenueData
    splitValueAmongstObjects(revenueData, value, 'revenueInPeriod')
}

const setHoursInPeriod = (hoursData, value, showAllPhases) => {
    const filteredHoursData =
        hoursData?.filter((d) => {
            const phase = PhaseCollection.phasesById[d.phaseId]
            const staff = StaffCollection.staffById[d.staffId]
            const role = RoleCollection.rolesById[d?.roleId] || staff?.role
            return (
                phase &&
                (showAllPhases
                    ? phase.status !== 'archived'
                    : phase.status === 'active') &&
                (!staff || !staff?.isArchived) &&
                (!role || !role?.isArchived)
            )
        }) || []
    hoursData = filteredHoursData.length ? filteredHoursData : hoursData

    // Weight by budget instead of existing hours
    const totalBudget = _.sum(hoursData?.map((d) => d.hoursBudget || 0) || [])
    if (totalBudget === 0) {
        // If no budget, distribute evenly
        splitValueAmongstObjects(hoursData, value, 'hoursInPeriod')
    } else {
        // Distribute proportionally to budget
        hoursData?.forEach((d) => {
            d.hoursInPeriod = ((d.hoursBudget || 0) / totalBudget) * value
        })
    }

    hoursData?.forEach((d) => {
        const project = ProjectCollection.projectsById[d.projectId]
        const phase = PhaseCollection.phasesById[d.phaseId]
        const staff = StaffCollection.staffById[d.staffId]
        const role = RoleCollection.rolesById[d?.roleId] || staff?.role
        const models = {
            staff,
            role,
            project,
            phase,
        }
        const rate = getCombinedRateInDateRange(models, 'cost', [
            phase?.startDate,
            phase?.endDate,
        ])
        d.costInPeriod = d.hoursInPeriod * rate
        const payRate = getCombinedRateInDateRange(models, 'pay', [
            phase?.startDate,
            phase?.endDate,
        ])
        const chargeOutRate = getCombinedRateInDateRange(models, 'chargeOut', [
            phase?.startDate,
            phase?.endDate,
        ])
        d.payInPeriod = d.hoursInPeriod * payRate
        d.chargeOutInPeriod = d.hoursInPeriod * chargeOutRate
    })
}

const setCostInPeriod = (hoursData, value, showAllPhases) => {
    hoursData =
        hoursData?.filter((d) => {
            const phase = PhaseCollection.phasesById[d.phaseId]
            return (
                phase &&
                (showAllPhases
                    ? phase.status !== 'archived'
                    : phase.status === 'active')
            )
        }) || []

    // Weight by cost budget instead of existing cost
    const totalCostBudget = getCostBudget(hoursData)
    if (totalCostBudget === 0) {
        // If no budget, distribute evenly
        splitValueAmongstObjects(hoursData, value, 'costInPeriod')
    } else {
        // Distribute proportionally to cost budget
        hoursData.forEach((d) => {
            const project = ProjectCollection.projectsById[d.projectId]
            const phase = PhaseCollection.phasesById[d.phaseId]
            const staff = StaffCollection.staffById[d.staffId]
            const role = RoleCollection.rolesById[d?.roleId] || staff?.role
            const models = {
                staff,
                role,
                project,
                phase,
            }
            const rate = getCombinedRateInDateRange(models, 'cost', [
                phase?.startDate,
                phase?.endDate,
            ])
            const costBudget = (d.hoursBudget || 0) * rate
            d.costInPeriod = (costBudget / totalCostBudget) * value
        })
    }

    hoursData.forEach((d) => {
        const project = ProjectCollection.projectsById[d.projectId]
        const phase = PhaseCollection.phasesById[d.phaseId]
        const staff = StaffCollection.staffById[d.staffId]
        const role = RoleCollection.rolesById[d?.roleId] || staff?.role
        const models = {
            staff,
            role,
            project,
            phase,
        }
        const rate = getCombinedRateInDateRange(models, 'cost', [
            phase?.startDate,
            phase?.endDate,
        ])
        d.hoursInPeriod = d.costInPeriod / rate
        const payRate = getCombinedRateInDateRange(models, 'pay', [
            phase?.startDate,
            phase?.endDate,
        ])
        const chargeOutRate = getCombinedRateInDateRange(models, 'chargeOut', [
            phase?.startDate,
            phase?.endDate,
        ])
        d.payInPeriod = d.hoursInPeriod * payRate
        d.chargeOutInPeriod = d.hoursInPeriod * chargeOutRate
    })
}

const getRevenueProgress = (revenueData) => {
    return (
        (getRevenueBeforePeriod(revenueData) +
            getRevenueInPeriod(revenueData)) /
            getTotalFee(revenueData) || 0
    )
}

const getCostProgress = (hoursData) => {
    return (
        (getCostBeforePeriod(hoursData) + getCostInPeriod(hoursData)) /
            getCostBudget(hoursData) || 0
    )
}

const setRevenueProgress = (revenueData, value, showAllPhases) => {
    revenueData =
        revenueData?.filter((d) => {
            const phase = PhaseCollection.phasesById[d.phaseId]
            return (
                phase &&
                (showAllPhases
                    ? phase.status !== 'archived'
                    : phase.status === 'active')
            )
        }) || []
    if (!isFinite(value) || !getTotalFee(revenueData)) return
    const previousRevenue = getRevenueBeforePeriod(revenueData)
    const totalRevenue = getTotalFee(revenueData) * value
    const newValue = Math.max(totalRevenue - previousRevenue, 0)
    setRevenueInPeriod(revenueData, newValue, showAllPhases)
}

const setCostProgress = (hoursData, value, showAllPhases) => {
    hoursData =
        hoursData?.filter((d) => {
            const phase = PhaseCollection.phasesById[d.phaseId]
            return (
                phase &&
                (showAllPhases
                    ? phase.status !== 'archived'
                    : phase.status === 'active')
            )
        }) || []
    if (!isFinite(value) || !getCostBudget(hoursData)) return
    const previousCost = getCostBeforePeriod(hoursData)
    const totalCost = getCostBudget(hoursData) * value
    const newValue = Math.max(totalCost - previousCost, 0)
    setCostInPeriod(hoursData, newValue, showAllPhases)
}

const setRevenueProgressFromCost = (revenueData, hoursData, showAllPhases) => {
    revenueData =
        revenueData?.filter((d) => {
            const phase = PhaseCollection.phasesById[d.phaseId]
            return (
                phase &&
                (showAllPhases
                    ? phase.status !== 'archived'
                    : phase.status === 'active')
            )
        }) || []
    hoursData = hoursData?.filter((d) => {
        const phase = PhaseCollection.phasesById[d.phaseId]
        return (
            phase &&
            (showAllPhases
                ? phase.status !== 'archived'
                : phase.status === 'active')
        )
    })
    revenueData.forEach((rd) => {
        const filteredHoursData = filterHoursData(
            hoursData,
            rd.staffId,
            rd.roleId,
            rd.projectId,
            rd.phaseId
        )
        const costProgress = getCostProgress(filteredHoursData)
        if (isFinite(costProgress)) {
            setRevenueProgress([rd], costProgress, showAllPhases)
        }
    })
}

const setCostProgressFromRevenue = (revenueData, hoursData, showAllPhases) => {
    revenueData =
        revenueData?.filter((d) => {
            const phase = PhaseCollection.phasesById[d.phaseId]
            return (
                phase &&
                phase.status &&
                (showAllPhases
                    ? phase.status !== 'archived'
                    : phase.status === 'active')
            )
        }) || []
    hoursData =
        hoursData?.filter((d) => {
            const phase = PhaseCollection.phasesById[d.phaseId]
            return (
                phase &&
                phase.status &&
                (showAllPhases
                    ? phase.status !== 'archived'
                    : phase.status === 'active')
            )
        }) || []
    hoursData.forEach((hd) => {
        const filteredRevenueData =
            revenueData?.filter((rd) => rd.phaseId === hd.phaseId) || []
        const revenueProgress = getRevenueProgress(filteredRevenueData)
        if (isFinite(revenueProgress)) {
            setCostProgress([hd], revenueProgress, showAllPhases)
        }
    })
}

const saveChanges = (id, queryData) => {
    const DataStore = require('../../State/DataStore').default
    DataStore.addSaveFunction(id, async () => {
        await makeRequest({
            baseURL: process.env.REACT_APP_NODE_SERVER_URL,
            path: `/project-forecast-sidebar/save`,
            method: 'POST',
            data: {
                ...queryData,
                organisationId: SessionStore.organisationId,
                userId: SessionStore.user?.id,
            },
        })
    })
}
